/**
 * Transient section membership preview during draw/drag/resize. Isolated from
 * `CanvasUiContext` so rAF-driven preview updates do not re-render all UI shell consumers.
 */

import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

export type SectionPreviewMode = "place" | "drag" | "resize";

export interface SectionPreviewState {
  memberIds: Set<string>;
  targetSectionId?: string;
  mode?: SectionPreviewMode;
}

export interface CanvasSectionPreviewContextType {
  sectionPreview: SectionPreviewState | null;
  setSectionPreview: Dispatch<SetStateAction<SectionPreviewState | null>>;
}

const CanvasSectionPreviewContext =
  createContext<CanvasSectionPreviewContextType | null>(null);

const CAPTURE_VALUE: CanvasSectionPreviewContextType = {
  sectionPreview: null,
  setSectionPreview: (() => {}) as Dispatch<
    SetStateAction<SectionPreviewState | null>
  >,
};

export function useCanvasSectionPreview(): CanvasSectionPreviewContextType {
  const ctx = useContext(CanvasSectionPreviewContext);
  if (!ctx) {
    throw new Error(
      "useCanvasSectionPreview must be used within CanvasSectionPreviewProvider",
    );
  }
  return ctx;
}

export function CanvasSectionPreviewProvider({
  children,
  variant = "interactive",
}: {
  children: ReactNode;
  variant?: "interactive" | "capture" | "view";
}) {
  const [sectionPreview, setSectionPreview] =
    useState<SectionPreviewState | null>(null);

  const interactiveValue = useMemo(
    (): CanvasSectionPreviewContextType => ({
      sectionPreview,
      setSectionPreview,
    }),
    [sectionPreview],
  );

  const value = variant === "capture" ? CAPTURE_VALUE : interactiveValue;

  return (
    <CanvasSectionPreviewContext.Provider value={value}>
      {children}
    </CanvasSectionPreviewContext.Provider>
  );
}
